import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { h2, link, paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import Textbox from '@stories/Components/Misc/Forms/Inputs/Textbox/Textbox';
import SectionWrapper from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Misc/SectionWrapper/SectionWrapper.styles';
import { m } from 'framer-motion';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  ${SectionWrapperStyles.Inner} {
    position: relative;
  }
`;
const FormWrapper = styled.div `
  position: relative;
`;
const Form = styled.form `
  ${transition('border-color')};

  --buttonX: 24px;
  --buttonSize: 24px;
  --buttonGap: 12px;
  --inputHeight: 70px;

  width: 100%;
  padding: 0;
`;
const Label = styled.label `
  ${h2};

  display: block;
  margin-bottom: 24px;

  ${Form}[data-sr-only='true'] & {
    ${srOnly};
  }
`;
const Button = styled(m.button) `
  ${ButtonReset};
  ${transition('opacity')};

  --primaryColour: ${brand.global.white};
  --contrastColour: ${brand.primary.blue};

  display: grid;
  place-content: center;
  width: var(--buttonSize);
  height: var(--buttonSize);
  border-radius: 50%;
  background-color: var(--primaryColour);
  color: var(--contrastColour);
  grid-column: 1 / span 1;
  grid-row: 1 / span 1;

  svg {
    display: block;
    width: 100%;
  }

  &:focus-visible {
    outline-style: solid;
    outline-offset: 2px;
    outline-width: 1px;
    outline-color: currentColor;
  }

  &:focus-visible,
  &:hover {
    opacity: 0.65 !important;
  }

  &[data-type='clear'] {
    --primaryColour: transparent;

    grid-column: 3;
    margin-left: 0;

    ${Form}:has(${ButtonStyles.Container}) & {
      grid-column: 2;
    }
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }
`;
const InputWrapper = styled.div `
  ${transition('border-color', 0.2)};

  display: grid;
  align-items: center;
  grid-template-columns: auto 1fr auto;
  border: 1px solid ${brand.grey.medium};
  background-color: ${brand.global.white};
  border-radius: 12px;
  padding: 0 var(--buttonX);
  gap: var(--buttonGap);

  ${ButtonStyles.Container} {
    --buttonHeight: calc(100% - 16px);
    grid-column: 3;
  }

  &:has(${ButtonStyles.Container}) {
    grid-template-columns: 1fr auto auto;
    padding-right: 8px;
  }

  &[data-error='true'] {
    border-color: ${brand.validation.invalid} !important;
  }
`;
const Input = styled(Textbox) `
  --bgColour: transparent !important;
  --borderRadius: 12px;
  --textColour: ${brand.primary.blue};
  --placeholderColour: ${rgba(brand.primary.blue, 0.5)};

  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin: 0;
  padding: 0;
  height: var(--inputHeight);
  font-size: 16px;
  line-height: 26px;
  grid-column: 2 / span 1;
  width: auto;
  border: 0;

  ${Form}:has(${ButtonStyles.Container}) & {
    grid-column: 1;
  }
`;
const Suggestions = styled(m.ul) `
  position: absolute;
  z-index: 1;
  top: calc(100% + 16px);
  left: 0;
  background-color: ${brand.global.white};
  box-shadow: 0px 1px 4px 0px ${rgba(brand.primary.blue, 0.1)};
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  width: 100%;

  @media ${from(Device.TabletLarge)} {
    min-width: 424px;
  }
`;
const AutosuggestOption = styled.button `
  ${ButtonReset};
  ${link};
  ${paragraphRegular};

  padding: ${fluid(8, 16)};
  text-align: left;
  margin: 0;
  color: ${brand.primary.blue};
  text-decoration-color: transparent;

  > span {
    font-weight: 700;
  }

  &:focus-visible,
  &:hover {
    text-decoration-color: transparent;
  }
`;
const SearchBarStyles = {
    AutosuggestOption,
    Button,
    Container,
    Form,
    FormWrapper,
    Input,
    Label,
    Suggestions,
    InputWrapper,
};
export default SearchBarStyles;
